import { Typography, Stack, Box, Chip } from '@mui/material';
import React from 'react';

/**
 * Fixd.ai Hero Section
 *
 * Modern, attractive design with visual elements
 * Minimal, direct, SEO-friendly messaging
 */
const FixdaiHeroSection: React.FC = () => (
  <Stack spacing={4} alignItems="center" maxWidth="1000px" sx={{ position: 'relative' }}>
    {/* Decorative dashed semi-circles - centered */}
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 700,
        height: 700,
        border: '2px dashed',
        borderColor: 'rgba(102, 126, 234, 0.12)',
        borderRadius: '50%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 850,
        height: 850,
        border: '2px dashed',
        borderColor: 'rgba(45, 127, 249, 0.08)',
        borderRadius: '50%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 1000,
        height: 1000,
        border: '2px dashed',
        borderColor: 'rgba(118, 75, 162, 0.06)',
        borderRadius: '50%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />

    {/* Content */}
    <Stack spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
      {/* Badge */}
      <Chip
        label="Free Cost Estimates • No Spam • No Sales Calls"
        size="medium"
        sx={{
          bgcolor: '#EFF6FF',
          color: '#2D7FF9',
          fontFamily: 'Matter, sans-serif',
          fontWeight: 600,
          fontSize: '0.875rem',
          px: 1,
          '&:hover': {
            bgcolor: '#DBEAFE',
          },
        }}
      />

      {/* Main Heading */}
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Matter, sans-serif',
          fontSize: { xs: '2rem', md: '3.25rem' },
          fontWeight: 700,
          color: 'text.primary',
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: '960px',
        }}
      >
        Plan Your Project With Confidence—From Costs to Permits to Pros
      </Typography>

      {/* Subheading */}
      <Typography
        variant="body1"
        color="text.secondary"
        fontWeight={400}
        fontSize={{ xs: '1rem', md: '1.125rem' }}
        sx={{
          fontFamily: 'Matter, sans-serif',
          mb: 2,
          px: 3,
          textAlign: 'center',
          maxWidth: '800px',
          lineHeight: 1.6,
        }}
      >
        FIXD analyzes your project, your township&apos;s permit requirements, and real contractor
        performance to deliver accurate cost estimates and trusted local pros—so you can start your
        home project with confidence.
      </Typography>

      {/* Process Steps */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 4 }}
        alignItems="center"
        justifyContent="center"
        sx={{
          mt: 3,
          flexWrap: 'wrap',
        }}
      >
        {[
          { step: '1', text: 'Describe Your Project' },
          { step: '2', text: 'Cost Research' },
          { step: '3', text: 'Permit Research' },
          { step: '4', text: 'Find Local Pros' },
        ].map((item, index) => (
          <Stack
            key={item.step}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              position: 'relative',
            }}
          >
            {/* Step number badge */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Matter, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(45, 127, 249, 0.3)',
              }}
            >
              {item.step}
            </Box>

            {/* Step text */}
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.primary"
              sx={{
                fontFamily: 'Matter, sans-serif',
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              {item.text}
            </Typography>

            {/* Arrow between steps (not on last item, not on mobile) */}
            {index < 3 && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  ml: 2,
                  color: '#CBD5E0',
                  fontFamily: 'Matter, sans-serif',
                }}
              >
                →
              </Box>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Stack>
);

export default FixdaiHeroSection;
